import type { ReactNode } from 'react';
import { Box } from '@mui/material';

import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { DASH } from './tokens';
import type { ProgramOverviewData } from './components/ProgramOverview';
import type { ReclassificationThresholds } from './components/ReclassificationPipeline';
import type { ProgramEffectivenessData } from './components/ProgramEffectiveness';
import type { DataHealthData } from './components/DataHealth';
import { LicenseRosteringBar } from './components/LicenseRosteringBar';
import { MonitoringRow } from './components/MonitoringRow';
import { GrowthTile } from '../Growth/GrowthTile';
import { DistrictStatsCard } from './components/DistrictStatsCard';
import { DistrictStatsComingSoonCard } from './components/DistrictStatsComingSoonCard';
import type { DistrictStats } from './districtStatsApi';
import type { LicenseRostering } from './licensesApi';
import type { BenchmarkMonitoring } from '../Benchmarks/types';
import type { SchoolRow } from './schools';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export interface DashboardData {
  licenseRostering?: LicenseRostering;
  benchmarkMonitoring?: BenchmarkMonitoring;
  districtStats?: DistrictStats;
  programOverview: ProgramOverviewData;
  thresholds?: ReclassificationThresholds;
  programEffectiveness?: ProgramEffectivenessData;
  schools?: SchoolRow[];
  dataHealth: DataHealthData;
}

// TEMPORARY: while the new dashboard is finalized, the report-backed
// dashboard shows only the checklist, the license bar, the District Stats card
// and the monitoring row. Program Overview, the exit note, the Exit Pipeline,
// Program Effectiveness, Schools and Data Health are held back; their data
// still arrives in DashboardData so they can return without a loader change.
export function Dashboard({ data, checklist }: { data: DashboardData; checklist?: ReactNode }) {
  const d = data;
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: DASH.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box sx={{ flex: 1, padding: '32px 24px' }}>
        <Box
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {checklist}
          {d.licenseRostering ? (
            <LicenseRosteringBar
              licenses={d.licenseRostering.licenses}
              rostered={d.licenseRostering.rostered}
            />
          ) : null}
          {d.districtStats ? (
            <DistrictStatsCard stats={d.districtStats} checklist={checklist === undefined ? undefined : null} />
          ) : (
            <DistrictStatsComingSoonCard />
          )}
          <GrowthTile />
          <MonitoringRow benchmarkMonitoring={d.benchmarkMonitoring} />
        </Box>
      </Box>
    </Box>
  );
}
